import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, MessageCircle, Send, Trash2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import PhoneFrame from '../components/PhoneFrame';
import PostCard from '../components/PostCard';
import StarField from '../components/StarField';
import { useApp } from '../context/AppContext';
import { createComment, deleteComment, fetchComments, fetchPostById } from '../lib/orbitApi';

export default function Comments() {
  const nav = useNavigate();
  const { postId } = useParams();
  const { authUser, profile } = useApp();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [loadedPost, loadedComments] = await Promise.all([
        fetchPostById(postId, authUser.id),
        fetchComments(postId),
      ]);
      setPost(loadedPost);
      setComments(loadedComments);
    } catch (err) {
      toast(err.message || 'Could not load comments');
    } finally {
      setLoading(false);
    }
  }, [postId, authUser.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const comment = await createComment(postId, body);
      setComments((rows) => [...rows, comment]);
      setPost((current) => current ? {
        ...current,
        counts: { ...current.counts, echoes: current.counts.echoes + 1 },
      } : current);
      setBody('');
    } catch (err) {
      toast(err.message || 'Could not post comment');
    } finally {
      setSending(false);
    }
  };

  const remove = async (commentId) => {
    setDeleting((value) => ({ ...value, [commentId]: true }));
    try {
      await deleteComment(commentId);
      setComments((rows) => rows.filter((comment) => comment.id !== commentId));
      setPost((current) => current ? {
        ...current,
        counts: { ...current.counts, echoes: Math.max(0, current.counts.echoes - 1) },
      } : current);
    } catch (err) {
      toast(err.message || 'Could not delete comment');
    } finally {
      setDeleting((value) => ({ ...value, [commentId]: false }));
    }
  };

  return (
    <PhoneFrame>
      <div className="relative min-h-full">
        <StarField />
        <div className="relative z-10 pb-8">
          <header className="orbit-page-header">
            <button className="press orbit-icon-button" onClick={() => nav(-1)} aria-label="Go back"><ArrowLeft /></button>
            <h1 className="font-display orbit-page-title flex-1 text-center">Comments</h1>
            <div className="w-10" aria-hidden="true" />
          </header>

          <div className="max-w-3xl mx-auto">
            {loading && <p className="text-center py-8 italic" style={{ color: '#8079a6' }}>Loading conversation…</p>}
            {!loading && !post && <p className="text-center py-8 italic" style={{ color: '#8079a6' }}>This broadcast is no longer available.</p>}
            {post && <PostCard post={post} onReactionChange={(delta) => setPost((current) => ({
              ...current,
              counts: { ...current.counts, stars: Math.max(0, current.counts.stars + delta) },
            }))} />}

            {post && (
              <form onSubmit={submit} className="orbit-surface-card mx-4 mb-4 flex items-start gap-3">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0" style={{ background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)' }}>
                    {(profile?.display_name || profile?.username || '?')[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <textarea
                    value={body}
                    onChange={(event) => setBody(event.target.value)}
                    maxLength={500}
                    rows={3}
                    placeholder="Add to the conversation…"
                    className="w-full bg-transparent outline-none resize-none"
                    style={{ color: '#efeaff' }}
                  />
                  <div className="flex items-center justify-between gap-3 mt-2">
                    <span className="text-xs" style={{ color: '#8079a6' }}>{body.length}/500</span>
                    <button type="submit" className="press orbit-primary-button !px-4" disabled={sending || !body.trim()}>
                      <Send /> {sending ? 'Sending…' : 'Comment'}
                    </button>
                  </div>
                </div>
              </form>
            )}

            {!loading && post && comments.length === 0 && (
              <div className="flex flex-col items-center py-8" style={{ color: '#8079a6' }}>
                <MessageCircle size={20} className="mb-2" />
                <p className="italic">No comments yet. Start the conversation.</p>
              </div>
            )}

            <div className="px-4 space-y-3">
              {comments.map((comment) => (
                <article key={comment.id} className="orbit-surface-card flex gap-3">
                  {comment.author.avatar ? (
                    <img src={comment.author.avatar} alt="" className="w-10 h-10 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold shrink-0" style={{ background: 'linear-gradient(135deg,#ff5bd1,#6b4fd6)' }}>
                      {(comment.author.name || comment.author.handle || '?')[0]?.toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong style={{ color: '#efeaff' }}>@{comment.author.handle}</strong>
                      <span className="text-xs" style={{ color: '#8079a6' }}>
                        {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words" style={{ color: '#e7dcff' }}>{comment.body}</p>
                  </div>
                  {comment.author.id === authUser.id && (
                    <button className="press orbit-icon-button" style={{ color: '#ff9d9d' }} onClick={() => remove(comment.id)} disabled={deleting[comment.id]} aria-label="Delete comment">
                      <Trash2 />
                    </button>
                  )}
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
